/* ==========================================================================
   SABR — Interactions des sections
   Custom elements natifs, aucune dépendance, chargés en defer.
   Chaque composant se dégrade proprement si le JS ne s'exécute pas.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- Carrousel : flèches, progression, clavier ------------------------- */
  class SabrCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-sabr-track]');
      this.prev = this.querySelector('[data-sabr-prev]');
      this.next = this.querySelector('[data-sabr-next]');
      this.bar = this.querySelector('[data-sabr-progress-bar]');
      if (!this.track) return;

      this.onScroll = this.throttle(this.update.bind(this), 100);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      if (this.prev) this.prev.addEventListener('click', () => this.scrollByPage(-1));
      if (this.next) this.next.addEventListener('click', () => this.scrollByPage(1));

      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.scrollByPage(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.scrollByPage(-1); }
      });

      this.update();
    }

    step() {
      var slide = this.track.querySelector('[data-sabr-slide]');
      if (!slide) return this.track.clientWidth;
      var gap = parseFloat(getComputedStyle(this.track).columnGap || 0) || 0;
      return slide.getBoundingClientRect().width + gap;
    }

    scrollByPage(dir) {
      var perView = Math.max(1, Math.floor(this.track.clientWidth / this.step()));
      this.track.scrollBy({
        left: dir * this.step() * perView,
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    }

    update() {
      var max = this.track.scrollWidth - this.track.clientWidth;
      var ratio = max > 2 ? this.track.scrollLeft / max : 1;
      if (this.bar) this.bar.style.transform = 'scaleX(' + Math.max(0.06, ratio || 0.06) + ')';
      var atStart = this.track.scrollLeft <= 2;
      var atEnd = this.track.scrollLeft >= max - 2;
      if (this.prev) this.prev.disabled = atStart;
      if (this.next) this.next.disabled = atEnd;
      this.toggleAttribute('data-scrollable', max > 2);
    }

    throttle(fn, wait) {
      var t = 0;
      return function () {
        var now = Date.now();
        if (now - t >= wait) { t = now; fn(); }
        else { clearTimeout(this._id); this._id = setTimeout(fn, wait); }
      };
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onScroll);
    }
  }

  /* --- Compteurs animés (chiffres clés) --------------------------------- */
  class SabrCounter extends HTMLElement {
    connectedCallback() {
      this.target = this.getAttribute('data-value') || '';
      var num = parseFloat(this.target.replace(/[^0-9.,]/g, '').replace(',', '.'));
      if (reduceMotion || isNaN(num) || !('IntersectionObserver' in window)) return;

      this.suffix = this.target.replace(/[0-9.,\s]/g, '');
      this.decimals = (String(num).split('.')[1] || '').length;
      this.final = num;
      this.textContent = this.format(0);

      this.io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { this.run(); this.io.disconnect(); }
        });
      }, { threshold: 0.4 });
      this.io.observe(this);
    }

    format(v) {
      return v.toFixed(this.decimals).replace('.', ',') + this.suffix;
    }

    run() {
      var start = performance.now();
      var dur = 1100;
      var tick = (now) => {
        var p = Math.min(1, (now - start) / dur);
        var eased = 1 - Math.pow(1 - p, 3);
        this.textContent = this.format(this.final * eased);
        if (p < 1) requestAnimationFrame(tick);
        else this.textContent = this.target;
      };
      requestAnimationFrame(tick);
    }
  }

  /* --- Sélecteur de coloris : change l'image sans recharger ------------- */
  class SabrSwatches extends HTMLElement {
    connectedCallback() {
      this.image = this.querySelector('[data-sabr-swatch-image]');
      this.buttons = this.querySelectorAll('[data-sabr-swatch]');
      this.priceEl = this.querySelector('[data-sabr-swatch-price]');
      if (!this.image || !this.buttons.length) return;

      this.buttons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.select(btn);
        });
      });
    }

    select(btn) {
      var src = btn.getAttribute('data-image');
      var srcset = btn.getAttribute('data-srcset');
      var label = btn.getAttribute('data-label');
      var price = btn.getAttribute('data-price');
      var url = btn.getAttribute('data-url');

      if (src) {
        this.image.style.opacity = '0';
        var swap = () => {
          this.image.src = src;
          if (srcset) this.image.srcset = srcset;
          if (label) this.image.alt = label;
          this.image.style.opacity = '';
        };
        reduceMotion ? swap() : setTimeout(swap, 160);
      }
      if (price && this.priceEl) this.priceEl.textContent = price;
      var link = this.querySelector('[data-sabr-swatch-link]');
      if (url && link) link.href = url;

      this.buttons.forEach((b) => {
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });
    }
  }

  /* --- Shop the look : points chauds sur le visuel ---------------------- */
  class SabrHotspots extends HTMLElement {
    connectedCallback() {
      this.dots = this.querySelectorAll('[data-sabr-hotspot]');
      this.cards = this.querySelectorAll('[data-sabr-hotspot-card]');
      if (!this.dots.length) return;

      this.dots.forEach((dot) => {
        var id = dot.getAttribute('data-sabr-hotspot');
        dot.addEventListener('click', () => this.activate(id));
        dot.addEventListener('mouseenter', () => this.activate(id));
        dot.addEventListener('focus', () => this.activate(id));
      });

      this.cards.forEach((card) => {
        var id = card.getAttribute('data-sabr-hotspot-card');
        card.addEventListener('mouseenter', () => this.activate(id));
      });
    }

    activate(id) {
      this.dots.forEach((d) => {
        d.classList.toggle('is-active', d.getAttribute('data-sabr-hotspot') === id);
        d.setAttribute('aria-expanded', d.getAttribute('data-sabr-hotspot') === id ? 'true' : 'false');
      });
      this.cards.forEach((c) => {
        var on = c.getAttribute('data-sabr-hotspot-card') === id;
        c.classList.toggle('is-active', on);
      });
    }
  }

  /* --- Guide des tailles : bascule cm / pouces -------------------------- */
  class SabrUnits extends HTMLElement {
    connectedCallback() {
      this.buttons = this.querySelectorAll('[data-sabr-unit]');
      this.values = this.querySelectorAll('[data-cm]');
      if (!this.buttons.length || !this.values.length) return;

      this.buttons.forEach((btn) => {
        btn.addEventListener('click', () => this.set(btn.getAttribute('data-sabr-unit'), btn));
      });
    }

    set(unit, btn) {
      this.values.forEach((el) => {
        var raw = el.getAttribute('data-cm') || '';
        if (unit === 'in') {
          el.textContent = raw.replace(/[\d.,]+/g, (n) => {
            var v = parseFloat(n.replace(',', '.'));
            return isNaN(v) ? n : (v / 2.54).toFixed(1).replace('.', ',');
          }).replace(/cm/gi, 'in');
        } else {
          el.textContent = raw;
        }
      });
      this.buttons.forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
    }
  }

  /* --- Révélation ligne par ligne des grands titres --------------------- */
  class SabrSplit extends HTMLElement {
    connectedCallback() {
      if (reduceMotion || !('IntersectionObserver' in window)) return;
      var target = this.firstElementChild;
      if (!target) return;

      var words = target.textContent.trim().split(/\s+/);
      if (words.length > 14) return;

      target.innerHTML = words
        .map(function (w, i) {
          return '<span class="sabr-split__word" style="--w:' + i + '">' + w + '</span>';
        })
        .join(' ');
      target.classList.add('sabr-split');

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.classList.add('is-revealed');
            io.unobserve(e.target);
          }
        });
      }, { threshold: 0.25 });
      io.observe(target);
    }
  }

  var defs = {
    'sabr-carousel': SabrCarousel,
    'sabr-counter': SabrCounter,
    'sabr-swatches': SabrSwatches,
    'sabr-hotspots': SabrHotspots,
    'sabr-units': SabrUnits,
    'sabr-split': SabrSplit
  };

  Object.keys(defs).forEach(function (name) {
    if (!customElements.get(name)) customElements.define(name, defs[name]);
  });
})();
